import {
  Box,
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { send } from "api/email.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
} from "common/constants";
import { SafeImage } from "components/SafeImage ";
import React from "react";
import { EmailNotification, PageProps } from "types/interface.types";
import { ContactLandingWrapper } from "./ContactUs.styles";
import { useStyles } from "./mantine.styles"; // adjust path as needed

const ContactUsPage: React.FC<PageProps> = () => {
  const { classes } = useStyles();

  // Define our useForm and must be declared before any UseEffects
  const form = useForm<EmailNotification>({
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const handleSubmit = async (values: EmailNotification) => {
    try {
      const json = await safeApiCall(send(values));
      if (json?.data?.error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_ERR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
        return;
      }

      if (json?.status === 200) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
        form.reset();
      }
    } catch (e: any) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: e?.message || "Something went wrong",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  return (
    <ContactLandingWrapper>
      <Paper shadow="md" radius="lg">
        <div className={classes.wrapper}>
          <div className={classes.contacts}>
            <Text size="lg" fw={700} className={classes.title} c={"#fff"}>
              Contact information
            </Text>

            <div className="contact-grp">
              <div>
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-at"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="12" cy="12" r="4"></circle>
                    <path d="M16 12v1.5a2.5 2.5 0 0 0 5 0v-1.5a9 9 0 1 0 -5.5 8.28"></path>
                  </svg>
                </div>
                <div>
                  <div className="contact-grp-label">Email</div>
                  <div className="contact-grp-text">info@jjl.co.nz</div>
                </div>
              </div>
              <div>
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-phone"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"></path>
                  </svg>
                </div>
                <div>
                  <div className="contact-grp-label">Phone</div>
                  <div className="contact-grp-text">+64 4 569 5248</div>
                  <div className="contact-grp-text">+64 21 326 626</div>
                </div>
              </div>
              <div>
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-map-pin"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="12" cy="11" r="3"></circle>
                    <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"></path>
                  </svg>
                </div>
                <div>
                  <div className="contact-grp-label">Address</div>
                  <div className="contact-grp-text">107 Hutt Park Road</div>
                  <div className="contact-grp-text">Gracefield</div>
                  <div className="contact-grp-text">Lower Hutt 5010</div>
                  <div className="contact-grp-text">New Zealand</div>
                </div>
              </div>
              <div>
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-sun"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <circle cx="12" cy="12" r="4"></circle>
                    <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7"></path>
                  </svg>
                </div>
                <div>
                  <div className="contact-grp-label">Trading hours</div>
                  <div className="contact-grp-text">8 a.m. – 5 p.m.</div>
                </div>
              </div>
            </div>
          </div>

          <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
            <Text size="lg" fw={700} className={classes.title}>
              Get in touch
            </Text>

            <div className={classes.fields}>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Your name"
                  placeholder="Your name"
                  required
                  {...form.getInputProps("name")}
                />
                <TextInput
                  label="Your email"
                  placeholder="Your email"
                  required
                  {...form.getInputProps("email")}
                />
              </SimpleGrid>

              <TextInput
                mt="md"
                label="Subject"
                placeholder="Subject"
                required
                {...form.getInputProps("subject")}
              />

              <Textarea
                mt="md"
                label="Your message"
                placeholder="Please include all relevant information"
                minRows={3}
                required
                {...form.getInputProps("message")}
              />

              <Group justify="flex-end" mt="md">
                <Button type="submit" className={classes.control}>
                  Send message
                </Button>
              </Group>

              <Divider
                my="xs"
                variant="dashed"
                labelPosition="center"
                label={
                  <>
                    {/* <Search size={12} /> */}seaerc
                    <Box ml={5}>Find us on Google Maps</Box>
                  </>
                }
              />
              <a
                target="_blank"
                href="https://www.google.com/maps/place/107+Hutt+Park+Road,+Gracefield,+Lower+Hutt+5010/@-41.2374266,174.9109133,17z"
                rel="noreferrer"
              >
                <SafeImage
                  src="images/jjl-location.png"
                  alt="JJL Address"
                  height={220}
                />
              </a>
            </div>
          </form>
        </div>
      </Paper>
    </ContactLandingWrapper>
  );
};

export default ContactUsPage;
